import type { ReactNode } from "react";

import RollingValue from "@/blueprint/components/RollingValue/RollingValue";
import Text from "@/blueprint/components/Text/Text";
import { cn } from "@/lib/utils";
import type { MaticModelId } from "@/views/shop/shopContent";
import { MODEL_SPEC_BAR } from "@/views/shop/shopContent";

const SpecItem = ({
  label,
  wraps = false,
  children,
  className,
}: {
  label: string;
  /**
   * Words may run to a second line rather than clip, should longer copy
   * ever outgrow the 420px column; today's fits on one line at every width.
   */
  wraps?: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex min-w-0 flex-col items-center gap-[2px] text-center",
      className,
    )}
  >
    <dt>
      <Text
        variant="paragraph-sm"
        tone="secondary"
        as="span"
        className="whitespace-nowrap text-spec-label tab:text-paragraph-sm"
      >
        {label}
      </Text>
    </dt>
    <dd>
      <Text
        variant="paragraph-md"
        as="span"
        className={cn(
          "flex items-baseline justify-center gap-xs text-spec-value font-medium",
          !wraps && "whitespace-nowrap",
        )}
      >
        {children}
      </Text>
    </dd>
  </div>
);

/**
 * The model spec bar at the top of the model block, where Tesla puts its
 * stats: label over value. Matic and Serene are the same hardware, so this
 * is where the choice shows: on a model change the noise figure rolls to
 * the new model's, while cleaning and assembly, the same for both, hold
 * still. Three facts in one row at every width, each on one line and
 * centred in an equal third; the price is the cards' to show. A quiet
 * filled block, as Tesla sets its stats on a faint panel, in the cards'
 * corner radius. The values are 14px Medium at every width; phones set
 * the labels a step smaller and the block 12px tighter top and bottom, to
 * keep cards in view.
 */
const ModelSpecBar = ({
  modelId,
  className,
}: {
  modelId: MaticModelId;
  className?: string;
}) => {
  return (
    <dl
      aria-label={MODEL_SPEC_BAR.label}
      className={cn(
        "grid grid-cols-3 items-start gap-x-s rounded-s bg-tint-black-5 px-s py-s tab:py-m",
        className,
      )}
    >
      <SpecItem label={MODEL_SPEC_BAR.noiseLabel}>
        <RollingValue
          value={MODEL_SPEC_BAR.noiseValue(MODEL_SPEC_BAR.noiseDb[modelId])}
        />
      </SpecItem>
      <SpecItem label={MODEL_SPEC_BAR.cleaningLabel}>
        {MODEL_SPEC_BAR.cleaning}
      </SpecItem>
      <SpecItem label={MODEL_SPEC_BAR.assemblyLabel}>
        {MODEL_SPEC_BAR.assembly}
      </SpecItem>
    </dl>
  );
};

export default ModelSpecBar;
